import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea, CRadioGroup } from "@supportinitiative/react-hook-form-mantine";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getConfirmationStatus, getSystemStatus } from "../../../UseCaseUtils";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UAction, USummaryStatsActionType, UseCaseType } from "@supportinitiative/common";
import { getId } from "src/utilities";
import { IconPlus, IconTrash } from "@tabler/icons-react";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

const USummaryStatsActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["SUMMARY_STATS"]),
  description: string().nullable(),
  definition: object({
    type: string().required().oneOf(["ERROR", "INFO", "WARNING"]),
    dataPopulation: array().of(
      object({
        name: string().required(),
        genFunction: string().required(),
        id: string().required(),
      }),
    ),
    header: string().required(),
    value: string().required(),
    valueType: string().nullable(),
    additionalContext: string().nullable(),
    increaseOrDecrease: string().nullable(),
    color: string().nullable(),
    chartGroup: string().nullable(),
  }),
});

function SummaryStats({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<USummaryStatsActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        type: action.definition.type || "ERROR",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(USummaryStatsActionSchema),
  });

  const dataPopulation = useFieldArray({
    control,
    name: "definition.dataPopulation",
  });

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Summary Stats</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "ERROR",
                  label: "Error",
                },
                {
                  value: "INFO",
                  label: "Info",
                },
                {
                  value: "WARNING",
                  label: "Warning",
                },
              ]}
              showErrorText={false}
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Data Population" labelPosition="center" />
        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Columns
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                dataPopulation.append({
                  name: "",
                  genFunction: "",
                  id: getId(),
                });
              }}
              disabled={useCaseFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>
        <Stack gap="0">
          {dataPopulation.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`definition.dataPopulation.${index}.name`}
                    control={control}
                    label={"Name"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`definition.dataPopulation.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput
                    name={`definition.dataPopulation.${index}.id`}
                    control={control}
                    label={"Id"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      dataPopulation.remove(index);
                    }}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Divider my="xs" label="Details" labelPosition="center" />

        <Stack gap="0">
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CTextInput
                name={`definition.header`}
                control={control}
                label="Header"
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`definition.value`}
                control={control}
                label="Value"
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CSelect
                name={`definition.valueType`}
                control={control}
                label="Value Type"
                data={[
                  { label: "Text", value: "TEXT" },
                  { label: "Date", value: "DATE" },
                  { label: "Amount", value: "AMOUNT" },
                  { label: "Integer", value: "INTEGER" },
                ]}
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextInput
                name={`definition.additionalContext`}
                control={control}
                label="Additional Context"
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`definition.increaseOrDecrease`}
                control={control}
                label="Increase or Decrease"
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <CSelect
                name={`definition.color`}
                control={control}
                label="Color"
                data={[
                  { label: "Red", value: "RED" },
                  { label: "Pink", value: "PINK" },
                  { label: "Teal", value: "TEAL" },
                  { label: "Blue", value: "BLUE" },
                ]}
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`definition.chartGroup`}
                control={control}
                label="Chart Group"
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await USummaryStatsActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default SummaryStats;
